import AsyncStorage from "@react-native-async-storage/async-storage";
import {Dimensions, Platform, Alert, DeviceEventEmitter} from 'react-native';
import i18n from '../../i18n';
import NetInfo from '@react-native-community/netinfo';

// Conditionally import Android-specific modules
const ToastAndroid = Platform.OS === 'android' ? require('react-native').ToastAndroid : null;
import {generateObjectId} from "./objectId";
import {generateHappyId} from "./happyId";
import {getAccessToken, getUserInfo} from "./storage";
import VivaPaymentModule from "../paymentModules/VivaPaymentsModule";
import {RawBTPrintTicket, RawBTPrintKitchen} from "./rawBTDriver";
import {hungarianRoundNumbers} from "./hungarianRoundNumbers";
import { 
    triggerStoreOrder, 
    checkOrderStored, 
    getLastOrderResponse 
} from './BackgroundTasks';
import ErrorReportingService from './ErrorReportingService';
import { 
    addToOfflineQueue, 
    isOnline, 
    triggerQueueProcessing,
    registerQueueProcessingTask
} from './OfflineQueue';
import FiscatQRService from './FiscatQRService';

const API_URL = 'https://api.happygastro.hu/api';

/**
 * Report an error to the server and handle it appropriately
 * @param {Error} error - The error object
 * @param {Object} context - Additional context information
 */
const reportError = async (error, context = {}) => {
  try {
    // Report the error to the server
    await ErrorReportingService.reportError(error, context);
  } catch (reportingError) {
    // Don't throw from the error reporter to avoid infinite loops
    console.error('Failed to report error:', reportingError);
  }
};


const {width, height} = Dimensions.get('window');

export const send_storeOrder = async (cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, isSavedForLater = false, alreadySentCartItems, orderId = generateObjectId(), paymentTypes, tip, originalAmount, selectedVipCard, userData, selectedItemsToPay, order, proform, deliveryFee) => {
    console.log("Send order to save: ", {
        orderType: (isSavedForLater || selectedItemsToPay?.length > 0 || currentTable ? "dineIn" : (userData?.orderType ? userData.orderType : "takeAway")),
        userData: userData,
        cartTotal: cartTotal,
        paymentType: paymentType,
        orderId: orderId,
        order: order
    });

    try {
        // Prepare order data for background task
        const orderData = {
            cartTotal,
            cartItems,
            paymentType,
            paidAmount,
            discountsForWholeCart,
            vatInvoiceData,
            needInvoice,
            currentTable,
            serviceFeeAmount,
            isSavedForLater,
            alreadySentCartItems,
            orderId,
            paymentTypes,
            tip,
            originalAmount,
            selectedVipCard,
            userData,
            selectedItemsToPay,
            order,
            proform,
            deliveryFee
        };

        // Check if device is online
        const online = await isOnline();

        if (!online) {
            console.log("Device is offline. Adding order to offline queue.");

            // Store the order in the offline queue
            const added = await addToOfflineQueue(orderData);

            if (added) {
                // Create a mock response for offline mode
                const mockResponse = {
                    _id: orderId,
                    orderCode: generateHappyId(),
                    items: [...(cartItems || []), ...(alreadySentCartItems || [])],
                    orderSum: cartTotal,
                    paymentType: paymentType,
                    paymentTypes: paymentTypes,
                    status: "pending_sync",
                    createdAt: new Date().toISOString(),
                    isOfflineOrder: true
                };

                // Store the mock response
                await AsyncStorage.setItem('lastOrderResponse', JSON.stringify(mockResponse));
                await AsyncStorage.setItem('orderStored', 'true');

                // Register the background task to process the queue when online
                await registerQueueProcessingTask();

                return mockResponse;
            } else {
                throw new Error("Failed to add order to offline queue");
            }
        }

        // If online, proceed with normal order processing
        // Trigger the background task to store the order
        await triggerStoreOrder(orderData);

        // Wait for the order to be stored
        let attempts = 0;
        const maxAttempts = 10;
        let isStored = false;

        while (!isStored && attempts < maxAttempts) {
            isStored = await checkOrderStored();
            if (!isStored) {
                // Wait a short time before checking again
                await new Promise(resolve => setTimeout(resolve, 300));
                attempts++;
            }
        }

        if (!isStored) {
            console.error("Order storage timed out");

            // If order storage timed out, try adding to offline queue as fallback
            console.log("Adding order to offline queue as fallback");
            const added = await addToOfflineQueue(orderData);

            if (added) {
                // Create a mock response for offline mode
                const mockResponse = {
                    _id: orderId,
                    orderCode: generateHappyId(),
                    items: [...(cartItems || []), ...(alreadySentCartItems || [])],
                    orderSum: cartTotal,
                    paymentType: paymentType,
                    paymentTypes: paymentTypes,
                    status: "pending_sync",
                    createdAt: new Date().toISOString(),
                    isOfflineOrder: true
                };

                // Store the mock response
                await AsyncStorage.setItem('lastOrderResponse', JSON.stringify(mockResponse));
                await AsyncStorage.setItem('orderStored', 'true');

                // Register the background task to process the queue when online
                await registerQueueProcessingTask();

                return mockResponse;
            } else {
                throw new Error("Order storage timed out and failed to add to offline queue");
            }
        }

        // Get the order response from AsyncStorage
        const orderResponse = await getLastOrderResponse();

        // Try to process any pending offline orders
        triggerQueueProcessing().catch(err => {
            console.error("Failed to process offline queue:", err);
        });

        return orderResponse;
    } catch (error) {
        console.error("Error in send_storeOrder:", error);
        // Report the error to the server if online
        if (await isOnline()) {
            await reportError(error, { 
                function: 'send_storeOrder',
                orderData: {
                    cartTotal,
                    orderId,
                    paymentType,
                    isSavedForLater
                }
            });
        }
        throw error;
    }
}

export const handleVivaPayment = (paidAmount) => {
    const paymentAmount = Number(paidAmount) * 100; // Amount in minor units (e.g., cents)
    return VivaPaymentModule.initiatePayment(paymentAmount);
};

export const handleNovoSzepPayment = async (paidAmount, sendToCashier, printReceiptAndTicket) => {
    const paymentAmount = Number(paidAmount) * 100; // Amount in minor units (e.g., cents)
    const novoTerminalSetting = await AsyncStorage.getItem('novoSettings');
    const processSuccess = () => {
        sendToCashier();
        printReceiptAndTicket();
    }};

export const handleTeyaPayment = async (paidAmount, sendToCashier, printReceiptAndTicket, orderCode = '') => {
    console.log("handleTeyaPayment called with paidAmount:", paidAmount);

    // Import the TeyaPaymentsModule
    const TeyaPaymentsModule = require('../paymentModules/TeyaPaymentsModule').default;

    try {
        // Use the TeyaPaymentsModule to initiate payment
        await TeyaPaymentsModule.initiatePayment(
            paidAmount, 
            0, // No tip amount
            orderCode,
            // onComplete callback
            async () => {
                console.log("Teya payment completed");
                // When payment is completed, call sendToCashier and printReceiptAndTicket
                await sendToCashier();
                await printReceiptAndTicket();
            },
            // onCancel callback
            () => {
                console.log("Teya payment cancelled");
                throw new Error('Payment cancelled');
            }
        );

        return true;
    } catch (error) {
        console.error("Error in handleTeyaPayment:", error);
        throw error;
    }
};


export const handlePayment = async (paymentType, paidAmount, originalAmount, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, onClose, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, successPayment = false, currentTable, serviceFeeAmount, alreadySentCartItems, orderId, paymentTypes, tip, selectedVipCard, selectedItemsToPay, order) => {
    if (!paymentType && (!paymentTypes || paymentTypes && paymentTypes.length === 0)) {
        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity('Kérlek válassz fizetési módot', 5000, ToastAndroid.CENTER);
        } else {
            setProcessing(false);
            return alert('Kérlek válassz fizetési módot');
        }
    }
    if(paymentTypes && paymentTypes.length > 0){
        const paymentsSum = paymentTypes.reduce((acc, curr) => { return acc + curr.amount }, 0)
        if (Math.round(paymentsSum) + 4 < Math.round(originalAmount) || (paymentType !== "Készpénz" && paymentsSum +4 < Math.round(originalAmount))) {
            if (Platform.OS === "android") {
                return ToastAndroid.showWithGravity('A fizetett összeg nem lehet kevesebb az eredeti összegnél', 5000, ToastAndroid.CENTER);
            } else {
                setProcessing(false);
                return alert('A fizetett összeg nem lehet kevesebb az eredeti összegnél');
            }
        }
    }else if (paidAmount + 4 < originalAmount || (paymentType !== "Készpénz" && paidAmount +4 < Math.round(originalAmount))) {
        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity('A fizetett összeg nem lehet kevesebb az eredeti összegnél', 5000, ToastAndroid.CENTER);
        } else {
            setProcessing(false);
            return alert('A fizetett összeg nem lehet kevesebb az eredeti összegnél');
        }
    }
    if (paidAmount === "") {
        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity('Kérlek add meg a fizetett összeget', 5000, ToastAndroid.CENTER);
        } else {
            setProcessing(false);
            return alert('Kérlek add meg a fizetett összeget');
        }
    }
    setProcessing(true);

    const storeOrderResponse = await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, false, alreadySentCartItems, orderId, paymentTypes, tip, originalAmount, selectedVipCard, undefined, selectedItemsToPay, order);
    //console.log("storeOrderResponse: ", storeOrderResponse);
    try {
        if (!storeOrderResponse) {
            if (Platform.OS === "android") {
                setProcessing(false);
                return ToastAndroid.showWithGravity('Nem sikerült a számla mentése!', ToastAndroid.LONG, ToastAndroid.CENTER, 50);
            } else {
                console.log("Nem sikerült a számla mentése!")
                setProcessing(false);
                return alert('Nem sikerült a számla mentése!');
            }
        }
    } catch (e) {
        setProcessing(false);
        // Report the error to the server
        await reportError(e, { 
            function: 'handlePayment',
            context: 'storeOrderResponse validation',
            paymentType,
            orderId
        });

        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity('Nem sikerült a számla mentése!', ToastAndroid.LONG, ToastAndroid.CENTER);
        } else {
            console.log("Nem sikerült a számla mentése!")
            return alert('Nem sikerült a számla mentése!');
        }
    }

    // Use the FiscatQRService to generate the QR code string
    const generateQRCodeString = (storedOrder) => {
        return FiscatQRService.generateQRCodeString(storedOrder);
    };
    const sendToCashier = () => {
        // Use the FiscatQRService to send the QR code to the cashier
        return FiscatQRService.sendToCashier(storeOrderResponse, sendData);
    };

    const printReceiptAndTicket = (order) => {
        if (Platform.OS !== "web") {
            const attemptPrinting = () => {
                try {
                    return new Promise((resolve, reject) => {
                        try {
                            // Print receipt tickets
                            RawBTPrintTicket(order, order.items);

                            if(cartItems.length > 0) {
                                setTimeout(() => {
                                    try {
                                        RawBTPrintKitchen(order, order.items);
                                        resolve(true);
                                    } catch (kitchenError) {
                                        reject(kitchenError);
                                    }
                                }, 500);
                            } else {
                                resolve(true);
                            }
                        } catch (e) {
                            reject(e);
                        }
                    });
                } catch (e) {
                    return Promise.reject(e);
                }
            };

            const showRetryDialog = () => {
                return new Promise((resolve) => {
                    if (Platform.OS === "android") {
                        Alert.alert(
                            i18n.t('error'),
                            i18n.t('printing_failed'),
                            [
                                {
                                    text: i18n.t('cancel'),
                                    onPress: () => {
                                        console.log('Printing cancelled');
                                        resolve(false); // Resolve with false to indicate cancellation
                                    },
                                    style: 'cancel',
                                },
                                {
                                    text: i18n.t('retry'),
                                    onPress: () => {
                                        console.log('Retrying printing');
                                        attemptPrinting()
                                            .then(() => resolve(true))
                                            .catch(e => {
                                                console.error('Retry printing failed:', e);
                                                showRetryDialog().then(result => resolve(result));
                                            });
                                    },
                                },
                            ],
                            { cancelable: false }
                        );
                    } else {
                        // For iOS or other platforms
                        alert(i18n.t('printing_failed'));
                        resolve(false); // Resolve with false to indicate failure
                    }
                });
            };

            // Return a promise that resolves when printing is complete or cancelled
            return new Promise((resolve) => {
                attemptPrinting()
                    .then(() => resolve(true))
                    .catch(e => {
                        console.error('Initial printing failed:', e);
                        showRetryDialog().then(result => resolve(result));
                    });
            });
        } else {
            // For web platform, just resolve immediately
            return Promise.resolve(true);
        }
    }

    try {
        console.log("Payment type is ", paymentType);
        if (paymentType === "Bankkártya") {
            return new Promise(async (resolve) => {
                const paymentTerminal = await AsyncStorage.getItem('selectedCardTerminal');
                console.log("Payment type is Bankkártya, paymentTerminal:", paymentTerminal, "successPayment:", successPayment);

                if (paymentTerminal && !successPayment && paymentTerminal === "Viva Payments") {
                    console.log("Using Viva Payments terminal");
                    return await handleVivaPayment(paidAmount, sendToCashier, printReceiptAndTicket);
                }
                if (paymentTerminal && !successPayment && paymentTerminal === "Novo Terminal") {
                    console.log("Using Novo Terminal");
                    return await handleNovoSzepPayment(paidAmount, sendToCashier, printReceiptAndTicket);
                }
                if (paymentTerminal && !successPayment && paymentTerminal === "Teya") {
                    console.log("Using Teya terminal");
                    return await handleTeyaPayment(paidAmount, sendToCashier, printReceiptAndTicket, orderId);
                }
                if(!paymentTerminal || successPayment) {
                    console.log("No payment terminal selected or successPayment is true");
                    // Use Promise.all to wait for both operations to complete
                    Promise.all([
                        sendToCashier(),
                        printReceiptAndTicket(storeOrderResponse)
                    ]).then(() => {
                        setCart([]);
                        setProcessing(false);
                        if (width >= 768) {
                            onClose();
                            navigation.navigate("HomeScreen", {paymentSuccess: true, orderResponse: storeOrderResponse});
                        } else {
                            navigation.navigate("SuccessPayment", {orderResponse: storeOrderResponse});
                        }
                        resolve();
                    }).catch(error => {
                        console.error("Error in payment process:", error);
                        // Report the error to the server
                        reportError(error, { 
                            function: 'handlePayment',
                            context: 'Bankkártya payment',
                            paymentType,
                            orderId
                        });
                        setProcessing(false);
                        resolve();
                    });
                }
            });
        }
        if (paymentType === "Szép kártya") {
            return new Promise(async (resolve) => {
                const paymentTerminal = await AsyncStorage.getItem('selectedCardTerminal');

                if (paymentTerminal && !successPayment && paymentTerminal === "Novo Terminal") return await handleNovoSzepPayment(paidAmount, sendToCashier, printReceiptAndTicket); // Implement this function as needed
                if(!paymentTerminal || successPayment) {
                    // Use Promise.all to wait for both operations to complete
                    Promise.all([
                        sendToCashier(),
                        printReceiptAndTicket(storeOrderResponse)
                    ]).then(() => {
                        setCart([]);
                        setProcessing(false);
                        if (width >= 768) {
                            onClose();
                            navigation.navigate("HomeScreen", {paymentSuccess: true, orderResponse: storeOrderResponse});
                        } else {
                            navigation.navigate("SuccessPayment", {orderResponse: storeOrderResponse});
                        }
                        resolve();
                    }).catch(error => {
                        console.error("Error in payment process:", error);
                        // Report the error to the server
                        reportError(error, { 
                            function: 'handlePayment',
                            context: 'Szép kártya payment',
                            paymentType,
                            orderId
                        });
                        setProcessing(false);
                        resolve();
                    });
                }
            });
        }

        if (paymentType === "Készpénz") {
            new Promise(() => sendToCashier());
            new Promise(() => printReceiptAndTicket(storeOrderResponse));
            setCart([]);
            if (width >= 768) {
                onClose();
                return navigation.navigate("HomeScreen", {paymentSuccess: true, orderResponse: storeOrderResponse});
            } else {
                return navigation.navigate("SuccessPayment", {orderResponse: storeOrderResponse});
            }
        }
        if (paymentType === "Utalvány") {
            return new Promise((resolve) => {
                // Use Promise.all to wait for both operations to complete
                Promise.all([
                    sendToCashier(),
                    printReceiptAndTicket(storeOrderResponse)
                ]).then(() => {
                    setCart([]);
                    setProcessing(false);
                    if (width >= 768) {
                        onClose();
                        navigation.navigate("HomeScreen", {paymentSuccess: true, orderResponse: storeOrderResponse});
                    } else {
                        navigation.navigate("SuccessPayment", {orderResponse: storeOrderResponse});
                    }
                    resolve();
                }).catch(error => {
                    console.error("Error in payment process:", error);
                    // Report the error to the server
                    reportError(error, { 
                        function: 'handlePayment',
                        context: 'Utalvány payment',
                        paymentType,
                        orderId
                    });
                    setProcessing(false);
                    resolve();
                });
            });
        }
        if (!paymentType && paymentTypes && paymentTypes.length > 0) {
            return new Promise((resolve) => {
                // Use Promise.all to wait for both operations to complete
                Promise.all([
                    sendToCashier(),
                    printReceiptAndTicket(storeOrderResponse)
                ]).then(() => {
                    setCart([]);
                    setProcessing(false);
                    if (width >= 768) {
                        onClose();
                        navigation.navigate("HomeScreen", {paymentSuccess: true, orderResponse: storeOrderResponse});
                    } else {
                        navigation.navigate("SuccessPayment", {orderResponse: storeOrderResponse});
                    }
                    resolve();
                }).catch(error => {
                    console.error("Error in payment process:", error);
                    // Report the error to the server
                    reportError(error, { 
                        function: 'handlePayment',
                        context: 'Multiple payment types',
                        paymentTypes: JSON.stringify(paymentTypes),
                        orderId
                    });
                    setProcessing(false);
                    resolve();
                });
            });
        }

    } catch (error) {
        setProcessing(false);
        console.log(error);

        // Report the error to the server
        await reportError(error, { 
            function: 'handlePayment',
            context: 'payment processing',
            paymentType,
            orderId,
            cartTotal,
            paidAmount
        });

        if (width >= 768) {
            onClose();
            return navigation.navigate("HomeScreen", {paymentSuccess: false, orderResponse: JSON.stringify(error)});
        } else {
            navigation.navigate("FailedPayment", {orderResponse: error});
        }
    } finally {
        setProcessing(false);
    }
};
